import React, { useState } from 'react';
import ImageLoader from '../components/ImageLoader';

interface GalleryItem {
  id: number;
  title: string;
  description: string;
  category: 'food' | 'restaurant' | 'farm' | 'culture' | 'people';
}

const Gallery: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'food' | 'restaurant' | 'farm' | 'culture' | 'people'>('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const galleryItems: GalleryItem[] = [
    {
      id: 0,
      title: "Crispy Seafood Platter",
      description: "Our chef's special crispy seafood platter with fresh herbs and vegetables, artfully arranged for maximum visual appeal",
      category: "food"
    },
    {
      id: 1,
      title: "Farm-to-Table Produce",
      description: "Fresh vegetables harvested from our organic farm, ensuring the highest quality ingredients for your dining experience",
      category: "farm"
    },
    {
      id: 2,
      title: "Marinated Beef Salad",
      description: "Thinly sliced marinated beef tossed with fresh herbs and crisp vegetables in our chef's special dressing",
      category: "food"
    },
    {
      id: 3,
      title: "Traditional Chicken Soup",
      description: "Soul-warming chicken soup with local vegetables, prepared according to an age-old Cambodian recipe",
      category: "food"
    },
    {
      id: 4,
      title: "Riverside Dining Experience",
      description: "Our peaceful outdoor dining area overlooking the serene waters, perfect for enjoying dinner under the stars",
      category: "restaurant"
    },
    {
      id: 5,
      title: "Vietnamese-Style Clear Soup",
      description: "A fragrant clear broth with fresh vegetables and herbs, showcasing the diverse culinary influences of Southeast Asia",
      category: "food"
    },
    {
      id: 6,
      title: "Spicy Stir-Fried Chicken",
      description: "Tender chicken pieces wok-fried with colorful vegetables and our secret blend of spices",
      category: "food"
    },
    {
      id: 7,
      title: "Authentic Khmer Sour Soup",
      description: "Traditional Cambodian sour soup with fresh herbs and local ingredients, a cornerstone of our culinary heritage",
      category: "food"
    },
    {
      id: 8,
      title: "Fish Amok in Banana Leaf",
      description: "Our signature dish - fresh fish steamed in banana leaf with a rich coconut curry paste, a royal Khmer specialty",
      category: "food"
    },
    {
      id: 9,
      title: "Cultural Performance Night",
      description: "Traditional Cambodian dance performances that take place weekly at our restaurant, celebrating local heritage",
      category: "culture"
    },
    {
      id: 10,
      title: "Restaurant Exterior at Night",
      description: "The warm, inviting atmosphere of our restaurant facade illuminated in the evening hours",
      category: "restaurant"
    },
    {
      id: 15,
      title: "Signature Beef Lok Lak",
      description: "Our chef's interpretation of the classic Cambodian dish with tender beef and special dipping sauce",
      category: "food"
    },
    {
      id: 16,
      title: "Our Organic Farm",
      description: "A view of our sustainable farm where we grow fresh produce for the restaurant, reducing food miles and ensuring quality",
      category: "farm"
    },
    {
      id: 17,
      title: "Farm Manager with Fresh Harvest",
      description: "Our dedicated farm manager proudly displaying the day's avocado harvest from our organic gardens",
      category: "people"
    },
    {
      id: 18,
      title: "Chef's Special Preparation",
      description: "Our head chef carefully preparing a special dish, showcasing the attention to detail that goes into every meal",
      category: "people"
    },
    {
      id: 19,
      title: "Choeung Ek Memorial Stupa",
      description: "A poignant view of the Memorial Stupa at Choeung Ek, commemorating the victims of the Cambodian genocide.",
      category: "culture"
    },
    {
      id: 20,
      title: "Stir-Fried Noodles",
      description: "Freshly prepared stir-fried noodles with seasonal vegetables and your choice of protein",
      category: "food"
    },
    {
      id: 21,
      title: "Farm Landscape View",
      description: "The beautiful surroundings of our farm set against the backdrop of Cambodia's lush landscape",
      category: "farm"
    },
    {
      id: 22,
      title: "Fragrant Lemongrass Soup",
      description: "Our aromatic lemongrass soup with fresh herbs that create layers of flavor with every spoonful",
      category: "food"
    },
    {
      id: 23,
      title: "Traditional Kitchen Techniques",
      description: "Our chefs using time-honored Cambodian cooking methods to prepare authentic dishes",
      category: "culture"
    },
    {
      id: 24,
      title: "Morning Harvest Activities",
      description: "The daily morning ritual of harvesting fresh produce for the day's menu",
      category: "farm"
    },
    {
      id: 25,
      title: "Staff Cultural Celebration",
      description: "Our team participating in local cultural festivities, strengthening our connection to Cambodian traditions",
      category: "culture"
    },
    {
      id: 26,
      title: "Local Farming Community",
      description: "Our partnership with local farmers who help supply additional ingredients for our kitchen",
      category: "people"
    }
  ];

  const filteredItems = activeCategory === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeCategory);

  const openLightbox = (index: number) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentImage((prev) => (prev === 0 ? filteredItems.length - 1 : prev - 1));
    } else {
      setCurrentImage((prev) => (prev === filteredItems.length - 1 ? 0 : prev + 1));
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16 bg-tuktuk-beige bg-opacity-10">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-center text-tuktuk-brown mb-4">
          Our Gallery
        </h1>
        <p className="text-center max-w-2xl mx-auto mb-12 text-lg">
          Step into the visual journey of Tuk Tuk Restaurant - from our farm-fresh ingredients to our carefully crafted dishes and the warm atmosphere we've created for our guests.
        </p>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'all'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('all')}
          >
            All
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'food'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('food')}
          >
            Food
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'restaurant'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('restaurant')}
          >
            Restaurant
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'farm'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('farm')}
          >
            Farm
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'culture'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('culture')}
          >
            Culture
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              activeCategory === 'people'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('people')}
          >
            People
          </button>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className="bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              onClick={() => openLightbox(filteredItems.indexOf(item))}
            >
              <div className="h-64 overflow-hidden">
                <ImageLoader 
                  index={item.id} 
                  alt={item.title} 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
            <button 
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300"
              onClick={closeLightbox}
            >
              &times;
            </button>
            <button 
              className="absolute left-4 text-white text-4xl hover:text-gray-300"
              onClick={() => navigateLightbox('prev')}
            >
              &#10094;
            </button>
            <button 
              className="absolute right-4 text-white text-4xl hover:text-gray-300"
              onClick={() => navigateLightbox('next')}
            >
              &#10095;
            </button>
            <div className="max-w-4xl max-h-90vh">
              <div className="relative">
                <ImageLoader 
                  index={filteredItems[currentImage].id} 
                  alt={filteredItems[currentImage].title}
                  className="max-w-full max-h-[80vh] mx-auto object-contain"
                />
                <div className="bg-black bg-opacity-70 text-white p-4 absolute bottom-0 left-0 right-0">
                  <h3 className="text-xl font-display font-bold mb-1">{filteredItems[currentImage].title}</h3>
                  <p>{filteredItems[currentImage].description}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery; 